import { useEffect, useState } from 'react';
import { measureEpisodePageCount } from '../utils/measureEpisodePageCount';
import { getReservedViewportHeight } from '../utils/viewportUtils';

interface EpisodeLike {
  sceneEpisodeId?: string;
  sceneEpisodeParagraphs?: Array<{ content?: string | null }>;
}

interface UseEpisodePagesMeasurementResult {
  episodePageCounts: number[];
  isMeasuring: boolean;
  error: Error | null;
  viewportWidth: number;
}

const createMeasurementHost = (viewportWidth: number, contentAreaHeight: number) => {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-9999px';
  host.style.top = '0';
  host.style.width = `${viewportWidth}px`;
  host.style.height = `${contentAreaHeight}px`;
  host.style.visibility = 'hidden';
  host.style.pointerEvents = 'none';
  host.style.overflow = 'visible';
  host.style.background = 'transparent';
  return host;
};

const waitForNextFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

const createEpisodeContentElement = (paragraphs: Array<{ content?: string | null }>) => {
  const content = document.createElement('div');
  content.style.height = '100%';
  content.style.display = 'inline-block';
  content.style.verticalAlign = 'top';
  content.style.overflow = 'hidden';
  content.style.columnWidth = '100vw';
  content.style.columnFill = 'auto';
  content.style.columnGap = '0';
  content.style.width = 'auto';

  const block = document.createElement('div');
  block.style.padding = '0 17px';

  paragraphs.forEach((paragraph) => {
    const p = document.createElement('p');
    p.textContent = paragraph.content ?? '';
    p.style.whiteSpace = 'pre-wrap';
    p.style.fontFamily = '"Noto Serif KR", ui-serif, Georgia, "Times New Roman", serif';
    p.style.fontSize = '18px';
    p.style.lineHeight = '40px';
    block.appendChild(p);
  });

  content.appendChild(block);
  return content;
};

export const useEpisodePagesMeasurement = (
  episodes: EpisodeLike[],
  sceneId: string | undefined,
  contentAreaHeight: number
): UseEpisodePagesMeasurementResult => {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const [episodePageCounts, setEpisodePageCounts] = useState<number[]>([]);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (
      !sceneId ||
      episodes.length === 0 ||
      viewportWidth <= 0 ||
      typeof document === 'undefined'
    ) {
      setEpisodePageCounts([]);
      return;
    }

    let isCancelled = false;

    const fullViewportHeight = typeof window !== 'undefined' ? window.innerHeight : 600;
    const reserved = getReservedViewportHeight();
    const fallbackContentHeight = Math.max(fullViewportHeight - reserved, fullViewportHeight * 0.6);
    const effectiveContentHeight =
      contentAreaHeight > 0 ? contentAreaHeight : fallbackContentHeight;

    const measure = async () => {
      setIsMeasuring(true);
      setError(null);

      const host = createMeasurementHost(viewportWidth, effectiveContentHeight);
      document.body.appendChild(host);

      const measuredPageCounts: number[] = Array(episodes.length).fill(1);

      try {
        for (let index = 0; index < episodes.length; index += 1) {
          const paragraphs = episodes[index]?.sceneEpisodeParagraphs ?? [];
          const contentElement = createEpisodeContentElement(paragraphs);
          host.appendChild(contentElement);
          await waitForNextFrame();

          measuredPageCounts[index] = measureEpisodePageCount(
            contentElement,
            effectiveContentHeight,
            viewportWidth
          );
          host.removeChild(contentElement);
        }
      } finally {
        document.body.removeChild(host);
      }

      if (!isCancelled) {
        setEpisodePageCounts(measuredPageCounts);
        setIsMeasuring(false);
      }
    };

    measure().catch((caughtError: unknown) => {
      if (isCancelled) return;
      setError(caughtError instanceof Error ? caughtError : new Error('측정에 실패했습니다.'));
      setEpisodePageCounts(Array(episodes.length).fill(1));
      setIsMeasuring(false);
    });

    return () => {
      isCancelled = true;
    };
  }, [episodes, sceneId, viewportWidth, contentAreaHeight]);

  return { episodePageCounts, isMeasuring, error, viewportWidth };
};
